import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { RequestParameters } from '~/backend/backend-client';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import { useFuseGeneratedImage } from '~/hooks/workflows/useFuseGeneratedImage';
import { Job } from '~/state/atoms/jobAtoms';
import {
    WorkflowTypes,
    pseudonymizeState,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useSessionID, useTranslationBackend } from '../useBackend';

/**
 * A React hook for creating an image translation task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export function useImageTranslation() {
    const primaryImage = useSelectedPrimaryImage();
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const imageTranslationClient = useTranslationBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const fuseGeneratedImage = useFuseGeneratedImage();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const finalFiles = job.payload?.pseudonymized
                ? await reidentifyFiles(job.results)
                : job.results;

            if (!finalFiles) {
                handleError('Re-identification failed for one or more files.');
                return;
            }

            const processedItems = await processCreatedFiles(
                finalFiles,
                job.task!.model
            );
            if (!processedItems) {
                return;
            }

            updateJob(job.id, { isImported: true });
            fuseGeneratedImage(processedItems);
        }
    };

    const handleSubmit = async (
        selectedModel: PredictionModel,
        customizationName?: string
    ) => {
        if (isSubmitting) {
            return;
        }

        if (primaryImage) {
            setIsSubmitting(true);
            try {
                resetSessionID();
                const taskUID = `${sessionID}.${v4().split('-')[0]}`;
                const blobs = Array.from(primaryImage.getSlicesAsDicomBlobs());
                const blobsToUpload = pseudonymize
                    ? await anonymizeBlobs(taskUID, blobs)
                    : blobs;

                const modelIdentifier = customizationName
                    ? `${selectedModel.name}.${customizationName}`
                    : selectedModel.name;

                const backendParameters: RequestParameters = {
                    action: modelIdentifier,
                    taskUID,
                    nExpectedFiles: blobs.length.toString(),
                };

                if (blobsToUpload) {
                    const newJobDetails: NewJobData = {
                        id: sessionID,
                        name: 'Image+',
                        totalItems: blobsToUpload.length,
                        type: WorkflowTypes.IMAGE,
                        client: imageTranslationClient,
                        onLoad: handleProcessedFiles,
                        payload: {
                            filesToUpload: blobsToUpload.map((file) => ({
                                file,
                            })),
                            backendParameters,
                            pseudonymized: pseudonymize,
                        },
                        task: {
                            model: selectedModel,
                        },
                    };

                    addJob(newJobDetails);
                    setWorkflowModalState((state) => ({
                        ...state,
                        open: false,
                    }));
                } else {
                    handleError('Anonymization failed for one or more files.');
                }
            } catch (error) {
                handleError(error, 'Failed to start image translation task.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return { startProcessing: handleSubmit, isSubmitting };
}
